import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Loader2, Plus, Save, Trash2, X, Shield, KeyRound } from "lucide-react";
import {
  listAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser,
  getAdminUser,
} from "../../../lib/admin";

/** Owner-only tab to manage admin panel accounts. */
const TeamTab = () => {
  const me = getAdminUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ username: "", password: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const [pwResetFor, setPwResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const reload = async () => {
    setLoading(true);
    try { setUsers(await listAdminUsers()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (me?.role !== "owner") {
    return (
      <div className="p-10 rounded-sm text-center" style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}>
        <Shield className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">Only the owner can manage team accounts.</p>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!draft.username.trim()) { toast.error("Username required"); return; }
    if (draft.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await createAdminUser({
        username: draft.username.trim().toLowerCase(),
        password: draft.password,
        role: draft.role,
      });
      toast.success(`Added ${draft.username}`);
      setDraft({ username: "", password: "", role: "admin" });
      setAdding(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };

  const handleRoleChange = async (u, role) => {
    if (u.role === role) return;
    try {
      await updateAdminUser(u.id, { role });
      toast.success(`Role updated to ${role}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change role");
    }
  };

  const handleDisable = async (u) => {
    try {
      await updateAdminUser(u.id, { disabled: !u.disabled });
      toast.success(u.disabled ? "Re-enabled" : "Disabled");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? They will lose access immediately.`)) return;
    try { await deleteAdminUser(u.id); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const handleResetPassword = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await updateAdminUser(pwResetFor.id, { password: newPw });
      toast.success(`Password reset for ${pwResetFor.username}. Share it securely.`);
      setPwResetFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-team-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Users size={20} /> Team Members
          </h2>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1">
            {users.length} account{users.length === 1 ? "" : "s"} · Owner can add/remove users and reset passwords.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="btn-brutal px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
          data-testid="add-user-btn"
        >
          <Plus size={14} /> Add user
        </button>
      </div>

      {adding && (
        <div className="p-4 rounded-sm" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }}>
          <div className="font-display text-[14px] font-bold mb-3">New user</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Username *</span>
              <input
                type="text" autoFocus
                value={draft.username}
                onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                placeholder="alex"
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="new-user-username"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Password *</span>
              <input
                type="text"
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                placeholder="Min 6 characters"
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px] font-mono"
                data-testid="new-user-password"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Role</span>
              <select
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="new-user-role"
              >
                <option value="admin">Admin (full access, no user management)</option>
                <option value="owner">Owner (full access + user management)</option>
              </select>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={() => { setAdding(false); setDraft({ username: "", password: "", role: "admin" }); }} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
              Cancel
            </button>
            <button onClick={handleCreate} disabled={busy} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="new-user-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Create
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="space-y-1">
          {users.map((u) => (
            <div
              key={u.id}
              className="flex items-center gap-3 p-3 rounded-sm flex-wrap"
              style={{
                background: u.disabled ? "#F5F5F4" : "#fff",
                border: "1px solid var(--ups-border)",
                opacity: u.disabled ? 0.6 : 1,
              }}
              data-testid={`user-row-${u.id}`}
            >
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[14px]">{u.username}</span>
                  {u.id === me?.id && <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">(you)</span>}
                  {u.disabled && <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-red-700">Disabled</span>}
                </div>
                <div className="text-[10px] font-mono text-[color:var(--ups-subtext)] mt-0.5">
                  {u.last_login_at ? `Last login: ${new Date(u.last_login_at).toLocaleString()}` : "Never logged in"}
                </div>
              </div>
              <select
                value={u.role}
                onChange={(e) => handleRoleChange(u, e.target.value)}
                disabled={u.id === me?.id}
                className="input-swiss px-2 py-1 rounded-sm text-[12px]"
                data-testid={`user-role-${u.id}`}
                title={u.id === me?.id ? "Can't change your own role" : ""}
              >
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
              <button
                onClick={() => { setPwResetFor(u); setNewPw(""); }}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1"
                style={{ border: "1px solid var(--ups-border)" }}
                title="Reset password"
                data-testid={`user-reset-pw-${u.id}`}
              >
                <KeyRound size={11} /> Reset PW
              </button>
              {u.id !== me?.id && (
                <>
                  <button
                    onClick={() => handleDisable(u)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
                    style={{ border: "1px solid var(--ups-border)" }}
                    data-testid={`user-toggle-disable-${u.id}`}
                  >
                    {u.disabled ? "Enable" : "Disable"}
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-red-50 text-red-700"
                    style={{ border: "1px solid #FCA5A5" }}
                    data-testid={`user-delete-${u.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {pwResetFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reset-pw-modal">
          <button className="absolute inset-0 bg-black/50" onClick={() => setPwResetFor(null)} aria-label="Close" />
          <div className="relative w-full max-w-sm rounded-sm" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
              <h3 className="font-display text-[16px] font-bold flex items-center gap-2"><KeyRound size={16} /> Reset password</h3>
              <button onClick={() => setPwResetFor(null)} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[12px]">New password for <strong>{pwResetFor.username}</strong>:</p>
              <input
                type="text" autoFocus
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Min 6 characters"
                className="input-swiss w-full px-2 py-2 rounded-sm text-[13px] font-mono"
                onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                data-testid="reset-pw-input"
              />
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setPwResetFor(null)} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>
                  Cancel
                </button>
                <button onClick={handleResetPassword} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5" data-testid="reset-pw-save">
                  <Save size={14} /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamTab;
